// Context hooks - replacing Apollo
import Box from "../../../components/Box/Box";
import Table from "../../../components/Table/Table";
import { utils } from "../../../helpers";
import { useResult } from "../../../shared/formContext/hooks/useResult";
import { useForm } from "../../../shared/formContext/hooks/useForm";

// Types
import {__} from "@wordpress/i18n";

/**
 * Generates table data for retirement investment evolution display
 *
 * @param data - Raw data array from retirement investment API
 * @param isMonthly - Whether to show monthly or yearly periods
 * @returns Table-compatible row data with formatted currency values
 */
const generateRetirementTableData = (data: any[], isMonthly: boolean): any[] => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => [
    {
      value: isMonthly ? `Mês ${item.period}` : `Ano ${item.year}`,
      className: "text-left",
    },
    { value: utils.toEuro(item.end_balance || 0, 2) },
  ]);
};

/**
 * CompoundInterestTable Component
 *
 * @param data - Period entries (monthly or yearly)
 * @param isOpen - Whether the Box starts expanded
 * @param isMonthly - Whether to show monthly or yearly periods
 */
const CompoundInterestTable: React.FC<{
  data: any[];
  isOpen: boolean;
  isMonthly: boolean;
}> = ({ data, isOpen, isMonthly }) => {
  // Table headers configuration
  const headers = [
    { 
      value: isMonthly ? __("Mês", "df-simulators") : __("Ano", "df-simulators"), 
      className: "text-left" 
    },
    { value: __("Montante", "df-simulators") },
  ];

  const tableData = generateRetirementTableData(data, isMonthly);

  return (
    <Box
      collapsible={{ open: isOpen }}
      noPadding
      header={{
        title: __("Evolução do investimento", "df-simulators"),
      }}
    >
      <Table headers={headers} data={tableData} />
    </Box>
  );
};

/**
 * CompoundInterestFooter Component
 * @returns JSX element containing the schedule tables or null if no data
 */
const CompoundInterestFooter: React.FC = () => {
  const { resultData } = useResult();
  const { formData } = useForm();
  const { response } = resultData;

  // Get the contribution frequency from submitted form data
  const contributionFrequency = formData.submitted?.contribution_frequency || 'monthly';
  const isMonthly = contributionFrequency === 'monthly';
  const retirementData = response?.data?.result?.data;

  // Use appropriate evolution data based on frequency
  const evolutionData = isMonthly 
    ? (retirementData?.evolution?.perPeriod || [])
    : (retirementData?.evolution?.perYear || []);

  return (retirementData && retirementData.evolution ?
    <div className="juros-compostos-footer">
        <CompoundInterestTable
          data={evolutionData}
          isOpen={true}
          isMonthly={isMonthly}
        />
    </div> : <></>
  );
};

export default CompoundInterestFooter;
